import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, getTwoLetterIsoName, getTwoLetterIsoLanguageName, getCapCode } from '@triumph/shared';
import request, { getHostname, getImageBaseUrl } from 'helpers/request';
import Label from 'components/_utility/Label';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCompletedSteps,
  getFinanceOption,
  getFinanceProviderUrl,
  getIsFinanceProviderCodeweavers,
  getSelectedColour,
  getLoadedConfiguration,
  getLabels,
  getAwaitingCalculation,
} from 'src/redux/app/selectors';
import { setCalculations, setFinanceOption, setAwaitingCalculation, setStep } from 'src/redux/app/actions';
import useDataLayer from 'src/hooks/useDataLayer';
import useLabel from 'src/hooks/useLabel';
import useOpenFinance from 'src/hooks/useOpenFinance';
import { STEPPER_HEIGHT } from 'src/config/constants';
import Loader from 'components/_utility/Loader/Loader';
import Modal from 'components/_utility/Modal/Modal';
import SaveQuoteModal from 'components/_modals/SaveQuoteModal/SaveQuoteModal';
import useCodeweavers from 'src/hooks/useCodeweavers';
import {
  Wrap,
  Title,
  Description,
  ToggleLabel,
  Breakdown,
  BreakdownTitle,
  BreakdownDescription,
  BreakdownImage,
  Summary,
  ChoiceOuter,
  ToggleGroup,
  BreakdownImageWrap,
  BreakdownImageLabels,
  BreakdownImageLabel,
  CalculatorButton,
  CalculatorIcon,
} from './FinanceOptions.styled';
const API_ORIGIN = window.location.origin.includes('localhost')
  ? 'https://www.triumph-test.info'
  : window.location.origin;
const SANTANDER_LOAN_TYPES = ['Loanballoon', 'Loan'];

const appendFinanceCalcScript = (url, setScriptLoading, provider) => {
  if (document.querySelector('[data-calc="true"]')) {
    return;
  }

  if (url === null || url === '') {
    return;
  }

  setScriptLoading(true);

  if (provider === 'santander') {
    document.body.append(document.createElement('scf-quoting'));
  }

  const scriptEl = document.createElement('script');
  scriptEl.src = url;
  scriptEl.dataset.calc = true;
  document.body.appendChild(scriptEl);

  scriptEl.addEventListener('load', () => setScriptLoading(false));

  return scriptEl;
};

const removeFinanceCalcScript = isSantander => {
  // Script shouldn't be removed for Santander at the moment.
  // See TRIUNSC-9 for more info.
  if (isSantander) {
    return;
  }

  const oldScript = document.querySelector('[data-calc="true"]');
  
  if (oldScript) {
    document.body.removeChild(oldScript);
  }

  // Is Santander
  if (isSantander) {
    const scfWrap = document.querySelector('scf-quoting');

    if (scfWrap) {
      document.body.removeChild(scfWrap);
    }

    if (window.scf?.timer) {
      clearTimeout(window.scf.timer);
    }

    window.scfSimulation = undefined;
    window.scfPlugin = undefined;
    window.ScfPlugin = undefined;
    window.scf = undefined;
  }
};

const FinanceOptions = ({ financeOptions = [], financeOptionContent, bikeModel }) => {
  const { price, thumbnail, colours, productCode, year, nameClean, canFinance } = bikeModel;
  const [scriptLoading, setScriptLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  
  const financeProviderUrl = useSelector(getFinanceProviderUrl);
  const isFinanceProviderCodeweavers = useSelector(getIsFinanceProviderCodeweavers);
  const selectedFinanceOption = useSelector(getFinanceOption);
  const [saveQuoteModalOpen, setSaveQuoteModalOpen] = useState(false);
  const dispatch = useDispatch();
  const getLabel = useLabel();
  const pushToDataLayer = useDataLayer();
  const wrapRef = useRef();
  const openFinance = useOpenFinance(bikeModel);
  const initCodeweavers = useCodeweavers();
  const bikeColour = useSelector(getSelectedColour);
  const completedSteps = useSelector(getCompletedSteps);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const awaitingCalculation = useSelector(getAwaitingCalculation);
  const labels = useSelector(getLabels);
  const capCode = getCapCode(colours, loadedConfiguration);
  const assetId = capCode || `${year}${productCode}${bikeColour?.colourCode}`;
  const bikePrice = loadedConfiguration?.price || price;

  const getAnalyticsEnabled = () => (window.OnetrustActiveGroups && window.OnetrustActiveGroups?.split(',').includes('C0002') ? true : false);  

  const handleSaveQuote = () => {
    pushToDataLayer('purchaseCalSec', { ctaText: getLabel('saveQuoteButtonLabel') });
    setSaveQuoteModalOpen(true);
  };

  const handleNextStep = () => {
    if (typeof window.scfSimulation === 'object' && selectedFinanceOption.calculatorEnabled) {
      // We can't hook into change events for Santander but we do know after they go to step 2
      // there is no longer the option to open the calc, so we can set the calculation safely here
      const { selectedSimulation } = window.scfSimulation;

      if (selectedSimulation) {
        window.localStorage.setItem('quoteRef', selectedSimulation.simulationChecksum);
        dispatch(setCalculations(selectedSimulation));
      }
    }

    if (!selectedFinanceOption?.calculatorEnabled) {
      // Codeweavers automatically saves calculations without interaction from the user.
      // If the user ends up on an option that doesn't use the finance calculator then clear
      // the calculation.
      dispatch(setCalculations(null));
    }

    dispatch(setStep(1));
  };

  const handleBuyNow = event => {
    event.preventDefault();

    if (window.codeweavers) {
      window.financeCalculator.closeCodeweaversModal();
    }

    if (window.scfSimulation) {
      window.scfSimulation.closeSimulator();
    }

    pushToDataLayer('purchaseCalPrim', { ctaText: getLabel('continueOrderLabel') });

    handleNextStep();
  };

  const hasSaveQuote = labels.saveQuoteButtonLabel?.length > 0;

  const santanderCalculatorParams = {
    financeFormText: getLabel('continueOrderLabel'),
    financeFormSecondaryText: hasSaveQuote ? getLabel('saveQuoteButtonLabel') : undefined,
    onPrimaryClick: (event) => handleBuyNow(event),
    onSecondaryClick: hasSaveQuote ? () => setSaveQuoteModalOpen(true) : undefined,
    bindLegacyQuoteModalEvents: false,
  };

  const clearQuote = useCallback(() => {
    window.localStorage.removeItem('quoteRef');
    window.localStorage.removeItem('quoteProductName');
    window.localStorage.removeItem('quoteMileage');
    window.localStorage.removeItem('quoteMonths');
    window.localStorage.removeItem('quoteDeposit');
  }, []);

  useEffect(() => {
    pushToDataLayer('purchaseFinanceOptions');

    if (completedSteps.length) {
      const y =
        wrapRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - STEPPER_HEIGHT;
      window.scrollTo(0, y);
    }

    if (selectedFinanceOption || financeProviderUrl.includes('codeweavers')) {
      appendFinanceCalcScript(financeProviderUrl, setScriptLoading, isFinanceProviderCodeweavers ? 'codeweavers' : 'santander');
    }
  }, []);

  useEffect(() => {
    if (scriptLoading || !bikeModel || (!bikeColour && !loadedConfiguration)) {
      return;
    }

    const lsBikeId = window.localStorage.getItem('bikeId');
    const lsBikePrice = Number(window.localStorage.getItem('bikePrice'));

    if (lsBikeId !== assetId) {
      window.localStorage.setItem('bikeId', assetId);
      clearQuote();
    }

    if (lsBikePrice !== bikePrice) {
      window.localStorage.setItem('bikePrice', bikePrice);
      clearQuote();
    }

    const quoteRef = window.localStorage.getItem('quoteRef');

    if (window.scf) {
      window.financeCalculator.setupScfModal(santanderCalculatorParams);

      window.financeCalculator.waitForScf().then(() => {
        window.financeCalculator.watchScfChanges();

        if (!window.OnetrustActiveGroups) {
          let attempts = 0;

          const waitForOnetrust = setInterval(() => {
            if (attempts >= 5) {
              return clearInterval(waitForOnetrust);
            }

            if (window.OnetrustActiveGroups) {
              window.scfSimulation.configure({
                cookie: getAnalyticsEnabled(),
              });

              return clearInterval(waitForOnetrust);
            }

            attempts += 1;
            return null;
          }, 1000);
        }

        // It still takes a bit of time for the calculator to be ready on Santander's end
        setTimeout(() => {
          dispatch(setAwaitingCalculation(false));
        }, 4000);
      });
    }

    if (
      window.codeweavers &&
      window.financeCalculator &&
      typeof window.financeCalculator.bindCodeweaversEvents === 'function'
    ) {
      // Add Buy Now + Save Quote events to Codeweavers modal markup (in public/index.html)
      window.financeCalculator.bindCodeweaversEvents({
        buyNowText: getLabel('continueOrderLabel'),
        onClick: handleBuyNow,
        saveQuoteText: getLabel('saveQuoteButtonLabel'),
        saveQuoteOnClick: handleSaveQuote,
      });
    }
  }, [scriptLoading, bikeModel, bikeColour, loadedConfiguration]);

  useEffect(() => {
    const { financeProductName } = selectedFinanceOption;

    if (window.scfSimulation) {
      window.financeCalculator.updateScfModalButtons(santanderCalculatorParams);

      dispatch(setAwaitingCalculation(true));

      let callbackIsStale = false;

      const timeout = setTimeout(() => dispatch(setAwaitingCalculation(false)), 4000);

      window.scfSimulation.scfSelectedSimulationUpdated(() => {
        if (callbackIsStale) {
          return;
        }

        clearTimeout(timeout);
        dispatch(setAwaitingCalculation(false))

        // Ideally we should be able to unregister this callback but the Santander calc doesn't provide an option for that
        callbackIsStale = true;
      });

      const urlImage = new URL(thumbnail?.src)

      const simulationChecksum = window?.scfSimulation?.selectedSimulation?.simulationChecksum || undefined

      window.scfSimulation.configure({
        assetId,
        country: getTwoLetterIsoName(),
        language: getTwoLetterIsoLanguageName(),
        assetPrice: bikePrice,
        assetImage: urlImage.href,
        cookie: getAnalyticsEnabled(),
        showButton: false,
        loanType: financeProductName,
        forceSelection: {
          loanType: financeProductName,
        },
        model: nameClean,
        simulationChecksum: window?.scfSimulation?.selectedSimulation?.simulationChecksum || undefined,
      });
    
      const isProcessing = false;


      if(loaded || !simulationChecksum) return
  
      window.scfSimulation.scfEmailMarketing(async detail => {
        setLoaded(true)
        const simulationID = simulationChecksum ||  'No data'
        if (!detail.emailSent && isProcessing) return;
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          mode: 'cors',
        };
        const contentRes = await request(`/api/v2/finance/quotecontent`, requestOptions);
        const postBody = {
          Email: detail.email,
          SimulationID: simulationID,
          Goal: contentRes?.data?.ViewGoalId || 'No data',
          CampaignId: contentRes?.data?.CampaignId || 'No data',
          ConsentEmail: detail.emailSent,
          BikeId: bikeModel.id,
          Link: `checkSum=${bikeModel.id}`,
        };
  
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(postBody),
        };
  
        fetch(`${API_ORIGIN}/api/v2/finance/quote`, options).then(response => response.json());
      });

    }

    if (financeProductName && !scriptLoading && window.codeweavers) {
      let cwQuoteRequestUrl = `${getHostname()}/api/v2/finance/getquotereference?AssetID=${assetId}&PaymentOption=${financeProductName}&Amount=${bikePrice}`;
      const lsQuoteMileage = window.localStorage.getItem('quoteMileage');
      const lsQuoteMonths = window.localStorage.getItem('quoteMonths');
      const lsQuoteDeposit = window.localStorage.getItem('quoteDeposit');
      dispatch(setAwaitingCalculation(true));

      if (lsQuoteMileage) {
        cwQuoteRequestUrl += `&mileage=${lsQuoteMileage}`;
      }

      if (lsQuoteMonths) {
        cwQuoteRequestUrl += `&months=${lsQuoteMonths}`;
      }

      if (lsQuoteDeposit) {
        cwQuoteRequestUrl += `&deposit=${lsQuoteDeposit}`;
      }

      fetch(cwQuoteRequestUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      })
        .then(res => res.json())
        .then(({ QuoteReference: quoteReference }) => {
          if (quoteReference) {
            window.localStorage.setItem('quoteRef', quoteReference);
            initCodeweavers();
          }
        });
    }

    if (!financeProductName) {
      dispatch(setCalculations(null));
    }
  }, [selectedFinanceOption, scriptLoading]);

  useEffect(() => {
    const interval = setInterval(() => {
      const storedProductName = window.localStorage.getItem('quoteProductName');
      const financeOption = financeOptions.find(({ financeProductName }) => financeProductName === storedProductName);

      if (selectedFinanceOption.financeProductName !== storedProductName && financeOption) {
        dispatch(setFinanceOption(financeOption.id));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    }
  }, [selectedFinanceOption]);

  return (
    <Wrap ref={wrapRef}>
      <Title>
        <Label name="financeOptionsTitle"></Label>
      </Title>
      <Description>
        <Label name="financeOptionsDescription"></Label>
      </Description>
      <ToggleLabel>
        <Label name="financeOptionsSubTitle"></Label>
      </ToggleLabel>
      <ChoiceOuter>
        <ToggleGroup
          options={financeOptions}
          onChange={financeOptionId => {
            const newFinanceOption = financeOptions.find(financeOption => financeOption.id === financeOptionId);

            dispatch(setFinanceOption(financeOptionId));
            pushToDataLayer('financeSelection', {
              financeProduct: newFinanceOption?.panelTitle,
            });
            window.localStorage.setItem('quoteProductName', newFinanceOption.financeProductName);

            // Script shouldn't be removed for Santander at the moment.
            // See TRIUNSC-9 for more info.
            
            // Is Santander
            // if (
            //   newFinanceOption.financeProductName &&
            //   SANTANDER_LOAN_TYPES.includes(newFinanceOption.financeProductName)
            // ) {
            //   // There's no way to update the Santander calculation so we have to destroy everything and re-init
            //   removeFinanceCalcScript(true);
            //   appendFinanceCalcScript(financeProviderUrl, setScriptLoading);
            // }
          }}
          selectedOptionId={selectedFinanceOption?.id}
        />
        {selectedFinanceOption ? (
          <Breakdown>
            <BreakdownTitle>{selectedFinanceOption.panelTitle}</BreakdownTitle>
            {selectedFinanceOption.didacticMention?.length > 0 && (
              <BreakdownDescription
                $highlight
                dangerouslySetInnerHTML={{ __html: selectedFinanceOption.didacticMention }}
              />
            )}
            <BreakdownDescription dangerouslySetInnerHTML={{ __html: selectedFinanceOption.paneldescription }} />
            {selectedFinanceOption.image?.src ? (
              <BreakdownImageWrap>
                <BreakdownImage
                  src={`${getImageBaseUrl()}${selectedFinanceOption.image.src}`}
                  alt={selectedFinanceOption.image.alt}
                />
                {selectedFinanceOption.optionOne ||
                selectedFinanceOption.optionTwo ||
                selectedFinanceOption.optionThree ? (
                  <BreakdownImageLabels
                    count={
                      [
                        selectedFinanceOption.optionOne,
                        selectedFinanceOption.optionTwo,
                        selectedFinanceOption.optionThree,
                      ].filter(val => val).length
                    }
                  >
                    {selectedFinanceOption.optionOne && (
                      <BreakdownImageLabel>{selectedFinanceOption.optionOne}</BreakdownImageLabel>
                    )}
                    {selectedFinanceOption.optionTwo && (
                      <BreakdownImageLabel>{selectedFinanceOption.optionTwo}</BreakdownImageLabel>
                    )}
                    {selectedFinanceOption.optionThree && (
                      <BreakdownImageLabel>{selectedFinanceOption.optionThree}</BreakdownImageLabel>
                    )}
                  </BreakdownImageLabels>
                ) : null}
              </BreakdownImageWrap>
            ) : null}
            {selectedFinanceOption.calculatorEnabled && canFinance && window?.scfSimulation?.selectedSimulation? (
              <CalculatorButton fluid onClick={() => openFinance()} disabled={!window.scfSimulation?.selectedSimulation}  >
                <CalculatorIcon />
                <Label name="financeCalculateButtonLabel"></Label>
              </CalculatorButton>
            ) : null}
          </Breakdown>
        ) : null}
      </ChoiceOuter>
      <Summary>{financeOptionContent.financeOptionsClosingText}</Summary>
      <Button fluid primary onClick={() => handleNextStep()} disabled={awaitingCalculation}>
        <Label name="financeContinueButtonLabel"></Label>
      </Button>
      {saveQuoteModalOpen ? (
        <Modal onClose={() => setSaveQuoteModalOpen(false)} disableFullScreen>
          <SaveQuoteModal
            primaryActionFunc={quoteRef => {
              window.localStorage.setItem('quoteRef', quoteRef);
            }}
            primarySuccessLabel="successLabel"
            secondaryActionFunc={() => {
              setSaveQuoteModalOpen(false);
              pushToDataLayer('saveQuoteCancel');
            }}
          ></SaveQuoteModal>
        </Modal>
      ) : null}
      {awaitingCalculation && <Loader />}
    </Wrap>
  );
};

export default FinanceOptions;
